import React, { useState, useEffect, useMemo } from "react";
import toast, { Toaster } from "react-hot-toast";
import GenericToaster from "./theme/create_toast/GenericToaster";
import ResponsiveDrawer from "./router/ResponsiveDrawer";
import AppRoutes from "./router/AppRoutes";
import AppDataInitializer from "./components/AppDataInitializer";
import Keycloak from "keycloak-js";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { InputBase } from "@mui/material";
import Store from "./Redux/store";
import { useDispatch, useSelector } from "react-redux";
import KeycloackForm from "./components/KeycloackForm";
import helpers from "./UtilitiesFunctions";
import CustomLogin from "./components/CustomLogin";
import { resolveUserRole } from "./utilities/userRoleUtils";

// Styled components using MUI v6 styled API
const Search = styled("div")(({ theme }) => ({
  color: "white",
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.87),
    color: "black",
  },
  marginRight: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(25),
    width: "50rem",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "20ch",
    },
  },
}));

const LoginContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  padding: theme.spacing(2),
}));

/*
  App Component Refactored to Functional Component for MUI v6 & React 18 Compatibility
*/
function App() {
  const dispatch = useDispatch();
  const theme = useTheme();

  // Redux Selectors
  const cart = useSelector((state) => state.cart);
  const global_config = useSelector((state) => state.globalState);
  const user_role = useSelector((state) => state.usersState.userRole);
  const reduxToken = useSelector((state) => state.usersState.token);
  const isAuthenticated = useSelector((state) => state.authReducer.isAuthenticated);
  const authUser = useSelector((state) => state.authReducer.user);
  const authToken = useSelector((state) => state.authReducer.token);

  const resolvedToken = useMemo(() => {
    const fromUsers = Array.isArray(reduxToken) ? reduxToken[0] : reduxToken;
    if (fromUsers && !(Array.isArray(fromUsers) && fromUsers.length === 0)) {
      return fromUsers;
    }
    return authToken || "";
  }, [reduxToken, authToken]);

  const hasPersistedSession = Boolean(isAuthenticated && resolvedToken);

  // Local State — hydrate from persisted Redux so refresh does not force login
  const [keycloak, setKeycloak] = useState(null);
  const [authenticated, setAuthenticated] = useState(hasPersistedSession);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(authUser || "");
  const [token, setToken] = useState(resolvedToken || "");
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [showLogin, setShowLogin] = useState(!hasPersistedSession);
  const [loginCredentials, setLoginCredentials] = useState({});

  // Keep local auth flags aligned with persisted Redux session
  useEffect(() => {
    if (isAuthenticated && resolvedToken) {
      setShowLogin(false);
      setAuthenticated(true);
      setToken(resolvedToken);
      if (authUser) setUserInfo(authUser);
    }
  }, [isAuthenticated, resolvedToken, authUser]);

  // Setup Keycloak instance
  const setupKeycloak = () => {
    const url = new URLSearchParams(document.location.search);
    var instance = url.get("appType") + "-";
    if (instance !== "null-") {
      dispatch({
        type: "GET_BASE_URL",
        payload: {
          appType: instance,
        },
      });
    }

    const newKeycloak = Keycloak({
      realm: global_config.companyName,
      url: global_config.keyCloakAuth,
      "ssl-required": "external",
      resource: global_config.appType + global_config.instanceId,
      clientId: global_config.appType + global_config.instanceId,
      "public-client": true,
      "verify-token-audience": true,
      "use-resource-role-mappings": true,
      "confidential-port": 0,
    });

    newKeycloak.resourceAccess = newKeycloak.resourceAccess || {};
    newKeycloak.resourceAccess.instance = instance;
    setKeycloak(newKeycloak);
    return newKeycloak;
  };

  // Handle successful login
  const handleLoginSuccess = (loginData) => {
    console.log("Login successful with data:", loginData);
    const accessToken =
      loginData.responseData?.access_token ||
      loginData.responseData?.token ||
      loginData.token ||
      null;

    const user = loginData.responseData?.user || {};
    const role = resolveUserRole(user, loginData.clientId);

    if (accessToken) {
      console.log("Using token authentication");
      dispatch({
        type: "GET_TOKEN",
        token: accessToken,
      });

      if (role) {
        dispatch({
          type: "SET_USER_ROLE",
          userRole: role,
        });
      }

      setLoginCredentials(loginData);
      setShowLogin(false);
      setAuthenticated(true);
      setToken(accessToken);
      setUserInfo(user);
      setKeycloak(null);

      console.log("Authentication state updated");
    } else {
      console.log("No token in response, showing error");
      toast.error("Authentication failed. No token received.");
    }
  };

  // Initialization logic
  useEffect(() => {
    const initialize = async () => {
      const url = new URLSearchParams(document.location.search);
      const companyName = url.get("companyName");
      const instanceId = url.get("instanceId");
      const appType = url.get("appType") || "";

      if (companyName != null && instanceId != null) {
        const state = Store.getState();
        const persistedCompany = state?.globalState?.companyName;
        const persistedInstance = state?.globalState?.instanceId;
        const tenantChanged =
          persistedCompany &&
          persistedInstance &&
          (String(persistedCompany) !== String(companyName) ||
            String(persistedInstance) !== String(instanceId));

        // Only wipe session when switching tenants — never on normal refresh
        if (tenantChanged) {
          helpers.clearData();
        }

        await dispatch({
          type: "GET_BASE_URL",
          payload: {
            companyName,
            instanceId,
            appType,
          },
        });
      }
      setupKeycloak();
    };

    initialize();

    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const logout = () => {
    helpers.clearData();
    dispatch({ type: "USER_LOGOUT" });
    setShowLogin(true);
    setAuthenticated(false);
    setUserInfo("");
    setToken("");
    setKeycloak(null);
  };

  // Render Logic
  const url = new URLSearchParams(document.location.search);
  const urlCompanyName = url.get("companyName");
  const urlInstanceId = url.get("instanceId");
  const effectiveCompanyName = urlCompanyName || global_config.companyName;

  if (!effectiveCompanyName) {
    return <KeycloackForm keycloak={keycloak} />;
  }

  if (showLogin) {
    return (
      <LoginContainer>
        <CustomLogin
          onLoginSuccess={handleLoginSuccess}
          companyName={effectiveCompanyName}
          appType={global_config.appType}
          instanceId={urlInstanceId || global_config.instanceId}
        />
      </LoginContainer>
    );
  }

  if (!authenticated) {
    return (
      <div className="blobcontainer">
        <div className="shapes"></div>
      </div>
    );
  }

  return (
    <>
      <Toaster
        position="bottom-right"
        reverseOrder={false}
        toastOptions={{
          className: "",
          style: {
            boxShadow: "0 0 10px 0 rgba(0,0,0, 0.4)",
            fontWeight: "bold",
          },
        }}
      />
      <GenericToaster />
      <AppDataInitializer
        authenticated={isAuthenticated}
        token={reduxToken}
        appType={global_config.appType}
      />
      <ResponsiveDrawer
        userInfo={userInfo}
        token={reduxToken}
        cartlength={cart?.length}
        appType={global_config.appType}
        bussinesType={global_config.bussinessType}
        onLogout={logout}
        authenticated={isAuthenticated}
        userRole={user_role}
      >
        <AppRoutes
          authenticated={isAuthenticated}
          userRole={user_role}
          appType={global_config.appType}
        />
      </ResponsiveDrawer>
    </>
  );
}

export default App;
